import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  makeStyles,
  Grid,
  IconButton,
} from '@material-ui/core';

import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';

import {
  FloodIcon,
  DroughtTimelineIcon,
  NounWaterQualityIcon,
} from '../../../../assets/svg/Icon';

const useStyles = makeStyles((theme) => ({
  timelineItem: {
    '&::before': {
      flex: 0,
      padding: 0,
    },
    minHeight: 70,
  },
  eventCard: {
    padding: theme.spacing(2, 2.5),
    borderRadius: 8,
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    backgroundColor: '#fff',
    border: '1px solid #e5e7eb',
    marginBottom: theme.spacing(1.5),
  },
  eventContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  eventIcon: {
    width: 56,
    height: 56,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    flexShrink: 0,
  },
  eventInfo: {
    flex: 1,
    minWidth: 0,
  },
  eventTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: theme.spacing(0.5),
    lineHeight: 1.4,
    fontFamily: 'Sarabun, sans-serif',
  },
  eventDate: {
    fontSize: '0.7rem',
    color: '#6b7280',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  eventRight: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flexShrink: 0,
  },
  areaSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    width: '100%',
    flexWrap: 'nowrap',
  },
  areaLabel: {
    fontSize: '0.875rem',
    color: '#6b7280',
    whiteSpace: 'nowrap',
  },
  areaChips: {
    display: 'flex',
    gap: theme.spacing(0.75),
    alignItems: 'center',
  },
  areaChip: {
    borderRadius: 6,
    backgroundColor: '#E0E0E0',
    color: '#000',
    padding: '4px 10px',
    fontSize: '0.8rem',
    fontWeight: 500,
    whiteSpace: 'nowrap',
  },
  moreButton: {
    padding: 6,
    color: '#6b7280',
  },
  detailButton: {
    fontFamily: 'Sarabun, sans-serif',
    fontSize: '0.8rem',
    textTransform: 'none',
    borderRadius: 6,
    padding: theme.spacing(0, 1),
    borderColor: '#d1d5db',
    color: '#374151',
    '&:hover': {
      borderColor: '#9ca3af',
      backgroundColor: '#f9fafb',
    },
  },
}));

export default function EventItem({ event }) {
  const classes = useStyles();
  const history = useHistory();
  // ใช้ไอคอนตามประเภทเหตุการณ์
  const getEventIcon = (eventType) => {
    switch (eventType) {
      case 1:
        return <FloodIcon style={{ fontSize: '50px' }} />;
      case 2:
        return <DroughtTimelineIcon style={{ fontSize: '40px' }} />;
      case 3:
        return <NounWaterQualityIcon style={{ fontSize: '35px' }} />;
      default:
        return <FloodIcon style={{ fontSize: '50px' }} />;
    }
  };

  const iconConfig = {
    icon: getEventIcon(event.event_type?.id),
    bgColor: '#f5f5f5',
  };

  return (
    <>
      <Box className={classes.eventCard}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12} sm={6}>
            <Box display="flex">
              {/* Icon Section */}
              <Box
                className={classes.eventIcon}
                mr={2}
              >
                {iconConfig.icon}
              </Box>

              {/* Info Section */}
              <Box className={classes.eventInfo}>
                <Typography className={classes.eventTitle}>
                  {event.event_name || event.title}
                </Typography>
                <Box className={classes.eventDate}>
                  <CalendarTodayIcon style={{ fontSize: 16 }} />
                  {moment(event.start_date).locale('th').format('D MMM YYYY')}
                  -
                  {moment(event.end_date).locale('th').format('D MMM YYYY')}
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Right Section */}
          <Grid item xs={12} sm>
            {/* Area Tags */}
            <Box className={classes.areaSection}>
              <Typography className={classes.areaLabel}>พื้นที่</Typography>
              <Box className={classes.areaChips}>
                {(event.areas || []).slice(0, 2).map((area) => (
                  <Box key={area.id} className={classes.areaChip}>
                    {area.label}
                  </Box>
                ))}
              </Box>
              {/* More Button - แสดงเฉพาะเมื่อมีพื้นที่มากกว่า 2 */}
              {(event.areas || []).length > 2 && (
                <IconButton className={classes.moreButton} size="small">
                  <MoreHorizIcon
                    onClick={() => {
                      history.push(`/${process.env.REACT_APP_PATH}reportwater/reports/${event.id}`);
                    }}
                  />
                </IconButton>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} sm="auto" className={classes.eventRight}>
            {/* Detail Button */}
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                history.push(`/${process.env.REACT_APP_PATH}reportwater/reports/${event.id}`);
              }}
              className={classes.detailButton}
            >
              ดูรายละเอียด
            </Button>
          </Grid>
        </Grid>
      </Box>

    </>
  );
}

EventItem.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]).isRequired,

    event_name: PropTypes.string,
    title: PropTypes.string,

    start_date: PropTypes.string,
    end_date: PropTypes.string,

    event_type: PropTypes.shape({
      id: PropTypes.number,
    }),

    areas: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
      }),
    ),
  }).isRequired,
};
